import { getContactsFromPrivateDoc } from './studentPrivateUtils.js'

/**
 * Returns the value for a given field, prioritizing sources:
 *   1. student.overrides[field] (Admin overrides - highest priority)
 *   2. student.app[category][field] (App form data - categorized)
 *   3. student.app[field] (App form data - legacy flat)
 *   4. student.aeries[field] (Aeries import data)
 *   5. student.seis[field] (SEIS import data)
 *   6. top-level student[field] (legacy/fallback - lowest priority)
 * @param {Object} student — Firestore student document data
 * @param {string} field — Field key to display (e.g. "firstName" or "programs.specialEducation")
 * @param {*} [fallback="—"] — Value to return if all sources are missing or empty
 * @returns {*} — The display value from the highest-priority source
 */
export function getDisplayValue(student, field, fallback = '—') {
  // Helper function to get nested object value
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : null
    }, obj)
  }

  // Admin overrides take highest priority
  const overrideValue = getNestedValue(student.overrides, field)
  if (student.overrides && overrideValue != null && overrideValue !== '') {
    return overrideValue
  }

  // App form data - check categorized structure first
  if (student.app) {
    // Student data fields
    if (
      [
        'firstName',
        'lastName',
        'grade',
        'plan',
        'planNote',
        'ssid',
        'caseManagerId',
        'isActive',
        'primaryDisability',
        'secondaryDisability'
      ].includes(field)
    ) {
      const appValue = getNestedValue(student.app, `studentData.${field}`)
      // For boolean fields (isActive), return the value even if it's false
      if (field === 'isActive' && appValue !== null && appValue !== undefined) {
        return appValue
      }
      // For other fields, check for non-empty values
      if (appValue != null && appValue !== '') {
        return appValue
      }
    }

    // Date fields
    if (['reviewDate', 'reevalDate', 'meetingDate'].includes(field)) {
      const appValue = getNestedValue(student.app, `dates.${field}`)
      if (appValue != null && appValue !== '') {
        return appValue
      }
    }

    // Accommodation fields
    if (['instruction', 'assessment'].includes(field)) {
      const appValue = getNestedValue(student.app, `accommodations.${field}`)
      if (appValue != null && appValue !== '') {
        return appValue
      }
    }

    // Legacy flat app structure (fallback)
    const appValue = getNestedValue(student.app, field)
    if (appValue != null && appValue !== '') {
      return appValue
    }
  }

  // Aeries import next
  const aeriesValue = getNestedValue(student.aeries, field)
  if (student.aeries && aeriesValue != null && aeriesValue !== '') {
    return aeriesValue
  }

  // SEIS import next
  const seisValue = getNestedValue(student.seis, field)
  if (student.seis && seisValue != null && seisValue !== '') {
    return seisValue
  }

  // Top-level field last (legacy/fallback)
  const topLevelValue = getNestedValue(student, field)
  if (topLevelValue != null && topLevelValue !== '') {
    return topLevelValue
  }

  return fallback
}

/**
 * Returns which source the displayed value came from.
 * @param {Object} student — Firestore student document data
 * @param {string} field — Field key to check
 * @returns {string} — One of "Override", "App", "Aeries", "SEIS", "Top-level", or "—"
 */
export function getSourceValue(student, field) {
  // Helper function to get nested object value
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : null
    }, obj)
  }

  const overrideValue = getNestedValue(student.overrides, field)
  if (student.overrides && overrideValue != null && overrideValue !== '') {
    return 'Override'
  }

  // App form data - check categorized structure first
  if (student.app) {
    // Student data fields
    if (
      [
        'firstName',
        'lastName',
        'grade',
        'plan',
        'primaryDisability',
        'secondaryDisability'
      ].includes(field)
    ) {
      const appValue = getNestedValue(student.app, `studentData.${field}`)
      if (appValue != null && appValue !== '') {
        return 'App'
      }
    }

    // Date fields
    if (['reviewDate', 'reevalDate', 'meetingDate'].includes(field)) {
      const appValue = getNestedValue(student.app, `dates.${field}`)
      if (appValue != null && appValue !== '') {
        return 'App'
      }
    }

    // Accommodation fields
    if (['instruction', 'assessment'].includes(field)) {
      const appValue = getNestedValue(student.app, `accommodations.${field}`)
      if (appValue != null && appValue !== '') {
        return 'App'
      }
    }

    // Legacy flat app structure (fallback)
    const appValue = getNestedValue(student.app, field)
    if (appValue != null && appValue !== '') {
      return 'App'
    }
  }

  const aeriesValue = getNestedValue(student.aeries, field)
  if (student.aeries && aeriesValue != null && aeriesValue !== '') {
    return 'Aeries'
  }

  const seisValue = getNestedValue(student.seis, field)
  if (student.seis && seisValue != null && seisValue !== '') {
    return 'SEIS'
  }

  const topLevelValue = getNestedValue(student, field)
  if (topLevelValue != null && topLevelValue !== '') {
    return 'Top-level'
  }

  return '—'
}

/**
 * Formats a student's display name, resolving values from the same
 * highest-priority sources as getDisplayValue (override → app → aeries → seis).
 *   'firstLast' → "Maria Gomez" — conversational contexts (form header, dialogs)
 *   'lastFirst' → "Gomez, Maria" — sorted lists/tables; matches lastName sort
 *     order so the alphabetical scan runs down the left edge of the column
 * @param {Object} student — Firestore student document data
 * @param {'firstLast'|'lastFirst'} [format='firstLast']
 * @returns {string}
 */
export function formatStudentName(student, format = 'firstLast') {
  const clean = (v) => (v == null || v === '—' ? '' : String(v).trim())
  const first = clean(getDisplayValue(student, 'firstName', ''))
  const last = clean(getDisplayValue(student, 'lastName', ''))
  if (!first) return last || '—'
  if (!last) return first
  return format === 'lastFirst' ? `${last}, ${first}` : `${first} ${last}`
}

/**
 * Convert a raw date-ish display value into a numeric sort key (ms epoch),
 * or null when the value is missing/unparseable.
 *
 * Sorting must never see NaN: a comparator that returns NaN is treated as
 * "equal", which makes the ordering non-transitive and inconsistent. Raw
 * values come from getDisplayValue, so they can be '—' (its fallback),
 * legacy MM/DD/YYYY strings from pre-normalization SEIS imports, or
 * { seconds, nanoseconds } maps from backups restored in-app.
 *
 * YYYY-MM-DD and MM/DD/YYYY parse as LOCAL midnight — new Date('YYYY-MM-DD')
 * parses as UTC and shifts the day in US timezones.
 *
 * @param {*} value — raw value from getDisplayValue
 * @returns {number|null}
 */
export function getDateSortKey(value) {
  if (value == null || value === '') return null

  if (value instanceof Date) {
    const t = value.getTime()
    return Number.isNaN(t) ? null : t
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }

  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') {
      const t = value.toDate().getTime()
      return Number.isNaN(t) ? null : t
    }
    const secs = value.seconds ?? value._seconds
    if (typeof secs === 'number') return secs * 1000
    return null
  }

  const str = String(value).trim()
  if (!str || str === '—') return null

  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])).getTime()
  }

  const mdy = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (mdy) {
    return new Date(Number(mdy[3]), Number(mdy[1]) - 1, Number(mdy[2])).getTime()
  }

  const t = new Date(str).getTime()
  return Number.isNaN(t) ? null : t
}

/**
 * Converts snake_case or underscore_separated keys to Title Case labels.
 * E.g. "first_name" → "First Name"
 * @param {string} field
 * @returns {string}
 */
export function labelize(field) {
  return field
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

/**
 * Whether inline list delimiters (;, -, *, numbered lists) should split items.
 * Missing flag defaults to ON so existing students keep current behavior.
 */
export function getAccommodationSplitDelimiters(studentOrFlag) {
  if (typeof studentOrFlag === 'boolean') return studentOrFlag
  if (studentOrFlag == null) return true
  return studentOrFlag.app?.accommodations?.splitDelimiters !== false
}

/**
 * Parse accommodation free-text into structured rows.
 * Always splits on newlines and recognizes [Header] lines.
 * When splitDelimiters is true (default), also splits on `;`, `- `, `* `, and `1. `.
 * Toggling the flag only changes parsing — it does not rewrite stored text.
 *
 * @param {string} text
 * @param {{ splitDelimiters?: boolean }} [options]
 * @returns {Array<{ text: string, isHeader: boolean }>}
 */
export function parseAccommodationText(text, options = {}) {
  if (!text || typeof text !== 'string') return []

  const splitDelimiters = options.splitDelimiters !== false
  const lines = text
    .split(/\n|\r/)
    .map((s) => s.trim())
    .filter(Boolean)
  const result = []

  for (const line of lines) {
    const headerMatch = line.match(/^\[(.+)\]$/)
    if (headerMatch) {
      result.push({ text: headerMatch[1], isHeader: true })
      continue
    }

    if (!splitDelimiters) {
      result.push({ text: line, isHeader: false })
      continue
    }

    const items = line
      .split(/\d+\.\s+|-\s+|\*\s+|;/)
      .map((s) => s.trim())
      .filter(Boolean)

    for (const item of items) {
      result.push({ text: item, isHeader: false })
    }
  }

  return result
}

// Accommodation text is user/import-controlled and rendered via v-html, so it
// must never reach the DOM as markup (stored XSS).
function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * @param {string} text
 * @param {{ splitDelimiters?: boolean }} [options]
 */
export function formatListFromText(text, options = {}) {
  if (!text) return '<div>—</div>'

  const parsed = parseAccommodationText(text, options)
  if (!parsed.length) return '<div>—</div>'

  let html = ''
  for (const item of parsed) {
    if (item.isHeader) {
      html += `<li class="accommodation-header">${escapeHtml(item.text)}</li>`
    } else {
      html += `<li class="accommodation-item">${escapeHtml(item.text)}</li>`
    }
  }

  return `<ul class="bullet-list">${html}</ul>`
}

/**
 * Formats accommodation text for CSV/export using dashes instead of bullet points.
 * @param {string} text
 * @param {{ splitDelimiters?: boolean }} [options]
 */
export function formatAccommodationsForExport(text, options = {}) {
  if (!text || typeof text !== 'string') return ''

  const parsed = parseAccommodationText(text, options)
  const result = []

  for (const item of parsed) {
    if (item.isHeader) {
      result.push(`${item.text}:`)
    } else {
      result.push(`- ${item.text}`)
    }
  }

  return result.join('\n')
}

/** @deprecated Use formatAccommodationsForExport */
export const formatAccommodationsForGoogleSheets = formatAccommodationsForExport

// Keep in sync with aeries504AccommodationDiff.js — typo-tolerant detection
// (preferrential/prefrential/prefered, seperate) without rewriting any text.
const PREFERENTIAL_SEATING_PATTERN =
  /pref(?:err?|r)ential\s+seat|pref\.?\s*seat|preff?err?e?d\s+seat/i
const SEPARATE_SETTING_PATTERN = /sep[ae]rate\s+sett?ings?|alternat(?:e|ive)\s+sett?ings?/i

/**
 * Suggest system flags when accommodation text mentions them but the flag is unchecked.
 * Searches both instruction and assessment fields.
 */
/** Preferential seating badge — flag1 or leftover alias. */
export function isPreferentialSeatingOn(flags = {}, student = null) {
  return !!(flags.flag1 || flags.preferentialSeating || student?.flag1)
}

/** Separate setting badge / testing roster — flag2 or leftover alias. */
export function isSeparateSettingOn(flags = {}, student = null) {
  return !!(flags.flag2 || flags.separateSetting || student?.flag2 || student?.separateSetting)
}

export function getAccommodationFlagHints({
  instruction = '',
  assessment = '',
  flag1 = false,
  flag2 = false
} = {}) {
  const combined = `${instruction}\n${assessment}`

  const mentionsPreferential = PREFERENTIAL_SEATING_PATTERN.test(combined)
  const mentionsSeparateSetting = SEPARATE_SETTING_PATTERN.test(combined)

  return {
    preferentialSeating: mentionsPreferential && !flag1,
    separateSetting: mentionsSeparateSetting && !flag2
  }
}

export function getStudentSchedule(student) {
  if (!student) return null
  // App schedule (new structure)
  if (student.app && student.app.schedule && student.app.schedule.periods) {
    return student.app.schedule.periods
  }
  // Aeries schedule.periods structure (your current format)
  if (student.aeries && student.aeries.schedule && student.aeries.schedule.periods) {
    return student.aeries.schedule.periods
  }
  // Aeries schedule (map "Period 1" -> "1", etc.)
  if (student.aeries && student.aeries.schedule) {
    const aeriesPeriods = student.aeries.schedule
    // Convert keys like "Period 1" to "1"
    const mapped = {}
    Object.keys(aeriesPeriods).forEach((key) => {
      const match = key.match(/^Period (\d+)$/)
      if (match) {
        mapped[match[1]] = aeriesPeriods[key]
      }
    })
    return Object.keys(mapped).length ? mapped : null
  }
  // Legacy schedule
  if (student.schedule) {
    return student.schedule
  }
  return null
}

/**
 * Primary / secondary eligibility from app form (preferred) or SEIS import.
 * Values are not shown in the roster row — only via intentional hover/click.
 */
export function getStudentEligibility(student) {
  if (!student) {
    return { primary: '', secondary: '', hasEligibility: false }
  }

  const primary =
    student.app?.studentData?.primaryDisability ||
    student.seis?.primaryDisability ||
    student.seis?.disability ||
    student.seis?.eligibilityCategory ||
    ''

  const secondary =
    student.app?.studentData?.secondaryDisability || student.seis?.secondaryDisability || ''

  const primaryTrimmed = String(primary).trim()
  const secondaryTrimmed = String(secondary).trim()

  return {
    primary: primaryTrimmed,
    secondary: secondaryTrimmed,
    hasEligibility: !!(primaryTrimmed || secondaryTrimmed)
  }
}

/**
 * Parent/guardian contacts for the student Contact dialog (legacy
 * `aeries.contacts` fallback when studentPrivate is missing).
 *
 * Prefers the structured `aeries.contacts` array. Falls back to the legacy
 * single-parent fields (`parentName/parentPhone/parentEmail`). Student
 * personal contact info is intentionally NOT returned.
 */
export function getStudentContacts(student) {
  const aeries = student?.aeries || {}
  if (Array.isArray(aeries.contacts) && aeries.contacts.length) {
    return getContactsFromPrivateDoc({ guardianContacts: aeries.contacts })
  }
  if (aeries.parentName || aeries.parentPhone || aeries.parentEmail) {
    return getContactsFromPrivateDoc({
      legacyGuardian: {
        name: aeries.parentName,
        phone: aeries.parentPhone,
        email: aeries.parentEmail
      }
    })
  }
  return []
}

export function hasStudentContacts(student) {
  return getStudentContacts(student).length > 0
}

/**
 * The open eval's INITIAL meeting date, mirrored onto the student by the
 * evalCaseManagerSync Cloud Function (app.evalMeetingDate). Empty when no
 * open eval or no meeting date entered yet.
 */
export function getEvalMeetingDate(student) {
  return String(student?.app?.evalMeetingDate || '').trim()
}

/**
 * Meeting date shown in the student table / badges.
 *
 * An open evaluation's meeting date wins: that IS the student's next meeting,
 * and it comes from the eval record (CF mirror), not the meetings summary —
 * so the orphan-date guard below does not apply to it.
 *
 * When calendar sync is on, `app.dates.meetingDate` is a denormalized summary of
 * upcoming `meetings` docs (see useMeetings.updateStudentSummary). Orphan values
 * left by seed/import/manual entry (meetingDate set, meetingCount 0/missing)
 * must not display as a scheduled meeting.
 *
 * When sync is off, meetingDate is a plain manual field — return it as-is.
 *
 * @param {Object} student
 * @param {boolean} calendarSyncOn
 * @param {*} [fallback='']
 */
export function getMeetingDateDisplay(student, calendarSyncOn = false, fallback = '') {
  const evalMeeting = getEvalMeetingDate(student)
  if (evalMeeting) return evalMeeting
  if (calendarSyncOn) {
    const count = Number(student?.app?.dates?.meetingCount || 0)
    if (count <= 0) return fallback
  }
  const value = getDisplayValue(student, 'meetingDate', '')
  return value != null && value !== '' && value !== '—' ? value : fallback
}
